import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Grid, Stack, Typography, CircularProgress, Chip } from "@mui/material";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import useHome from "../../../../../hooks/user/useHome";
import useAuth from "../../../../../hooks/useAuth";
import WAFStatusCard from "./components/WAFStatusCard";
import WAFSettingsDrawer from "./components/modal/WAFSettingsDrawer";

const WAFConfigurationStatus = () => {
  const { isAuthenticated } = useAuth();
  const { wafOverview, getWafOverview, setErr, curProjectId } = useHome();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      // Clear so project switches show loading instead of stale org totals.
      getWafOverview();
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getWafOverview, curProjectId]);

  const isLoading = wafOverview === null;

  const wafStats = useMemo(() => {
    if (!wafOverview) {
      return {
        total: 0,
        signatureWafEnforced: 0,
        smartParseWafEnforced: 0,
        sitesWithAnyProtection: 0,
        anyProtection: false,
      };
    }
    const total = wafOverview.totalSites || 0;
    const signatureWafEnforced = wafOverview.sdSigWaf?.enabled || 0;
    const smartParseWafEnforced = wafOverview.smartParseWaf?.enabled || 0;
    const sitesWithAnyProtection = Math.max(signatureWafEnforced, smartParseWafEnforced);
    const anyProtection = sitesWithAnyProtection > 0;
    return { total, signatureWafEnforced, smartParseWafEnforced, sitesWithAnyProtection, anyProtection };
  }, [wafOverview]);

  const formatPercentage = (enforced, total) => {
    if (total === 0) return "0%";
    return `${Math.round((enforced / total) * 100)}%`;
  };

  const handleEdit = (type) => {
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerType(null);
  };

  const handleDrawerSave = () => {
    getWafOverview();
  };

  return (
    <>
      <Helmet title="WAF Overview" />
      <Box sx={{ p: 6, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
        <Stack spacing={6}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <V2PageHeader
              title="WAF Overview"
              description="Protect your applications with our Web Application Firewall. Monitor and configure protection modules to defend against SQL injection, XSS, and other web vulnerabilities."
              sx={{ pb: 0, flex: 1, minWidth: 0 }}
            />
            {!isLoading && (
              <Chip
                label={`${wafStats.sitesWithAnyProtection}/${wafStats.total} Sites Protected`}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  backgroundColor: wafStats.anyProtection ? colors.green[10] : colors.neutral[100],
                  color: wafStats.anyProtection ? colors.green[300] : colors.neutral[600],
                  border: `1px solid ${wafStats.anyProtection ? colors.green[200] : colors.neutral[300]}`,
                  height: 32,
                  px: 1,
                }}
              />
            )}
          </Stack>

          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
              <CircularProgress size={40} sx={{ color: colors.primary[300] }} />
              <Typography 
                sx={{ 
                  fontSize: 14, 
                  color: colors.neutral[500],
                  mt: 2,
                }}
              >
                Loading WAF configuration...
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={6}>
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography 
                    sx={{ 
                      fontSize: 18, 
                      fontWeight: 600, 
                      color: colors.neutral[900] 
                    }}
                  >
                    Core Protection
                  </Typography>
                  <Chip
                    label="Recommended"
                    size="small"
                    sx={{
                      fontSize: 11,
                      fontWeight: 500,
                      backgroundColor: colors.primary[100],
                      color: colors.primary[300],
                      height: 22,
                    }}
                  />
                </Stack>
                <Grid container spacing={4}>
                  <Grid item xs={12} lg={6}>
                    <WAFStatusCard 
                      type="smartparse"
                      enabled={wafStats.smartParseWafEnforced > 0}
                      settings={{
                        "Protected": `${wafStats.smartParseWafEnforced}/${wafStats.total}`,
                        "Coverage": formatPercentage(wafStats.smartParseWafEnforced, wafStats.total),
                      }}
                      variant="featured"
                      onEdit={() => handleEdit("smartparse")}
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Stack spacing={3}>
                <Typography 
                  sx={{ 
                    fontSize: 18, 
                    fontWeight: 600, 
                    color: colors.neutral[900] 
                  }}
                >
                  Additional Protection
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <WAFStatusCard 
                      type="signature"
                      enabled={wafStats.signatureWafEnforced > 0}
                      settings={{
                        "Protected": `${wafStats.signatureWafEnforced}/${wafStats.total}`,
                        "Coverage": formatPercentage(wafStats.signatureWafEnforced, wafStats.total),
                      }}
                      variant="standard"
                      onEdit={() => handleEdit("signature")}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>
      {drawerType && (
        <WAFSettingsDrawer
          type={drawerType}
          open={drawerOpen}
          handleClose={handleDrawerClose}
          onSave={handleDrawerSave}
        />
      )}
    </>
  );
};

export default WAFConfigurationStatus;
